// useCheatingFeatureFlag — frontend hook for runtime cheating-feature flag checks.
// Mirrors the SearchJobAI implementation but lives here so the candidate-side
// coding test (Web-Interviewer frontend) can read flags without depending on
// the recruiter app's code.
//
// Architecture:
//   * Single fetch to /api/cheating-features/public on first mount.
//   * Module-level shared cache + 10-min TTL.
//   * Safe defaults: every unknown flag → DISABLED.
//   * Imperative API for non-hook callers (detector init code).

import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const CLIENT_TTL_MS = 10 * 60 * 1000;

let _cache = null;
let _cachedAt = 0;
let _inFlight = null;
const _listeners = new Set();

function isFresh() {
  return _cache !== null && Date.now() - _cachedAt < CLIENT_TTL_MS;
}

async function loadOnce(force = false) {
  if (!force && isFresh()) return _cache;
  if (_inFlight) return _inFlight;

  _inFlight = axios
    .get('/api/cheating-features/public')
    .then((res) => {
      _cache = (res.data && res.data.features) || {};
      _cachedAt = Date.now();
      _listeners.forEach((fn) => fn(_cache));
      return _cache;
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[proctoring] flag fetch failed:', err?.message || err);
      if (_cache === null) _cache = {};
      return _cache;
    })
    .finally(() => {
      _inFlight = null;
    });

  return _inFlight;
}

/** Force-invalidate the client cache and re-fetch. */
export function refreshCheatingFeatureFlags() {
  _cachedAt = 0;
  return loadOnce(true);
}

/** Synchronous read for non-hook callers. */
export function isCheatingFeatureEnabled(featureKey) {
  if (!_cache) return false;
  return Boolean(_cache[featureKey]?.enabled);
}

export function getCheatingFeatureThreshold(featureKey) {
  if (!_cache) return {};
  return _cache[featureKey]?.threshold || {};
}

/** Hook returning a snapshot of all flags. */
export function useCheatingFeatureFlags() {
  const [snapshot, setSnapshot] = useState(_cache);

  useEffect(() => {
    let cancelled = false;
    loadOnce().then((c) => {
      if (!cancelled) setSnapshot(c);
    });
    const sub = (c) => {
      if (!cancelled) setSnapshot({ ...c });
    };
    _listeners.add(sub);
    return () => {
      cancelled = true;
      _listeners.delete(sub);
    };
  }, []);

  const refresh = useCallback(() => refreshCheatingFeatureFlags(), []);
  return { flags: snapshot || {}, loading: snapshot === null, refresh };
}

/** Hook for a single feature. */
export function useCheatingFeatureFlag(featureKey) {
  const { flags, loading } = useCheatingFeatureFlags();
  const row = flags ? flags[featureKey] : null;
  return {
    isEnabled: Boolean(row?.enabled),
    threshold: row?.threshold || {},
    loading,
  };
}

export default useCheatingFeatureFlag;
